/* ==========================================================================
   JOVAN — JavaScript del tema (sin dependencias)
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const seg = (p, a, b) => clamp((p - a) / (b - a));
  const lerp = (a, b, t) => a + (b - a) * t;
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const mix = (a, b, t) => ({ x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t), r: lerp(a.r, b.r, t), s: lerp(a.s, b.s, t) });
  const reduceMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches || theme.animations === false;

  /* ---------- Dinero ---------- */
  function formatMoney(cents, format) {
    format = format || theme.moneyFormat || '${{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, precision, thousands = ',', decimal = '.') => {
      if (n == null || isNaN(n)) return '0';
      const parts = (n / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    };
    const match = format.match(placeholder);
    if (!match) return format;
    const map = {
      amount: () => fmt(cents, 2),
      amount_no_decimals: () => fmt(cents, 0),
      amount_with_comma_separator: () => fmt(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: () => fmt(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: () => fmt(cents, 0, ' ', ''),
      amount_with_space_separator: () => fmt(cents, 2, ' ', ','),
      amount_with_period_and_space_separator: () => fmt(cents, 2, ' ', '.')
    };
    return format.replace(placeholder, (map[match[1]] || map.amount)());
  }
  theme.formatMoney = formatMoney;

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message, isError = false) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('is-error', isError);
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }
  theme.toast = toast;

  /* ---------- Header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (header) {
      const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    const drawer = $('[data-menu-drawer]');
    if (!drawer) return;
    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const first = $('a, button', drawer.querySelector('.menu-drawer__panel'));
      if (first) first.focus();
    };
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };
    $$('[data-menu-open]').forEach((b) => b.addEventListener('click', open));
    $$('[data-menu-close]', drawer).forEach((b) => b.addEventListener('click', close));
    $$('a', drawer).forEach((a) => a.addEventListener('click', close));
    document.addEventListener('keydown', (e) => e.key === 'Escape' && close());
  }

  /* ---------- Revelado al hacer scroll + contadores ---------- */
  function animateCount(el) {
    const to = parseFloat(el.dataset.countTo);
    const decimals = parseInt(el.dataset.decimals || '0', 10);
    const prefix = el.dataset.prefix || '';
    const suffix = el.dataset.suffix || '';
    if (isNaN(to)) return;
    if (reduceMotion) {
      el.textContent = prefix + to.toFixed(decimals) + suffix;
      return;
    }
    const start = performance.now();
    const duration = 1600;
    const step = (now) => {
      const t = clamp((now - start) / duration);
      const v = to * (1 - Math.pow(1 - t, 4));
      el.textContent = prefix + v.toFixed(decimals) + suffix;
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  function initReveal(root = document) {
    const items = $$('[data-reveal], [data-count-to]', root);
    if (!('IntersectionObserver' in window) || reduceMotion) {
      items.forEach((el) => {
        el.classList.add('is-visible');
        if (el.dataset.countTo) animateCount(el);
      });
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          if (entry.target.dataset.countTo) animateCount(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    items.forEach((el) => io.observe(el));
  }

  /* ---------- Tarjetas con foco de luz ---------- */
  function initSpotlight() {
    document.addEventListener(
      'pointermove',
      (e) => {
        const card = e.target.closest && e.target.closest('.card');
        if (!card) return;
        const r = card.getBoundingClientRect();
        card.style.setProperty('--mx', `${e.clientX - r.left}px`);
        card.style.setProperty('--my', `${e.clientY - r.top}px`);
      },
      { passive: true }
    );
  }

  /* ---------- Cuenta regresiva (fecha real definida por la tienda) ---------- */
  function initCountdowns() {
    $$('[data-countdown]').forEach((el) => {
      const end = new Date(el.dataset.countdown).getTime();
      const wrap = el.closest('[data-countdown-wrap]') || el;
      if (isNaN(end)) {
        wrap.hidden = true;
        return;
      }
      const parts = $$('span', el);
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const diff = end - Date.now();
        if (diff <= 0) {
          wrap.hidden = true;
          clearInterval(timer);
          return;
        }
        const d = Math.floor(diff / 864e5);
        const h = Math.floor((diff % 864e5) / 36e5);
        const m = Math.floor((diff % 36e5) / 6e4);
        const s = Math.floor((diff % 6e4) / 1e3);
        const values = d > 0 ? [`${d}d`, pad(h), pad(m), pad(s)] : [pad(h), pad(m), pad(s)];
        parts.forEach((p, i) => {
          p.hidden = i >= values.length;
          if (values[i] != null) p.textContent = values[i];
        });
      };
      const timer = setInterval(tick, 1000);
      tick();
    });
  }

  /* ==========================================================================
     HERO: estuche M75 → pantalla LED → audífono sobre la oreja (con el scroll)
     ========================================================================== */
  class Hero {
    constructor(el) {
      this.el = el;
      this.svg = $('[data-hero-svg]', el);
      this.panels = $$('[data-hero-panel]', el);
      this.bars = $$('.hero__progress span', el);
      this.caseEl = $('[data-hero-case]', el);
      this.bud = $('[data-hero-bud]', el);
      this.budTop = $('[data-hero-bud-top]', el);
      this.lid = $('[data-hero-lid]', el);
      this.lidInner = $('[data-hero-lid-inner]', el);
      this.led = $('[data-hero-led]', el);
      this.noise = $('[data-hero-noise]', el);
      this.word = $('[data-hero-word]', el);
      const videoWrap = $('[data-hero-video]', el);
      this.video = videoWrap ? $('video', videoWrap) : null;
      this.caps = $$('[data-hero-cap]', el);
      this.stage = $('.hero__stage', el);
      this.lastLed = -1;
      this.cap = -1;
      // La animación corre sola (no depende del scroll): el estuche se abre,
      // el audífono va a la oreja y se queda sonando. Dura ~4 s.
      this.duration = 3600;

      if (reduceMotion) {
        this.render(0.5);
        return;
      }

      this.tick = this.tick.bind(this);
      this.render(0);
      if (this.stage) this.stage.addEventListener('click', () => this.start());
      // Arranca cuando el hero está en pantalla, después de que la página pinta.
      const io = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        setTimeout(() => this.start(), 300);
      });
      io.observe(el);
    }

    start() {
      cancelAnimationFrame(this.raf);
      this.t0 = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }

    tick(now) {
      const t = clamp((now - this.t0) / this.duration);
      this.render(t * 0.5);
      if (t < 1) this.raf = requestAnimationFrame(this.tick);
    }

    setCap(index) {
      if (index === this.cap) return;
      this.cap = index;
      this.caps.forEach((c, i) => c.classList.toggle('is-active', i === index));
    }

    render(p) {
      this.setCap(p < 0.17 ? 0 : p < 0.42 ? 1 : 2);

      if (this.video) {
        const d = this.video.duration;
        if (d && isFinite(d)) this.video.currentTime = Math.min(d - 0.05, p * d);
        return;
      }
      if (!this.svg || !this.bud || !this.lid) return;

      // 1) La tapa gira hacia atrás (pseudo-3D) y aparece su interior
      const lid = ease(seg(p, 0.03, 0.15));
      if (lid < 0.5) {
        this.lid.setAttribute('transform', `scale(1 ${(1 - lid * 2).toFixed(3)})`);
        this.lidInner.setAttribute('transform', 'scale(1 0)');
      } else {
        this.lid.setAttribute('transform', 'scale(1 0)');
        this.lidInner.setAttribute('transform', `scale(1 ${((lid - 0.5) * 2).toFixed(3)})`);
      }

      // 2) Pantalla LED: se enciende y cuenta hasta 100
      const lit = lid > 0.5;
      if (this.caseEl) this.caseEl.classList.toggle('is-lit', lit);
      if (this.led) {
        const value = lit ? Math.round(100 * clamp((lid - 0.5) * 2)) : 0;
        if (value !== this.lastLed) {
          this.led.textContent = String(value);
          this.lastLed = value;
        }
      }

      // 3) El audífono derecho sale del estuche y se coloca sobre la oreja
      const IN_CASE = { x: 254, y: 556, r: -25, s: 0.3 };
      const LIFT = { x: 300, y: 395, r: -12, s: 0.5 };
      const EAR = { x: 463.4, y: 336, r: 0, s: 0.92 };
      const OUT = { x: 528, y: 300, r: 12, s: 0.9 };

      let pos;
      if (p < 0.3) {
        pos = mix(IN_CASE, LIFT, ease(seg(p, 0.17, 0.29)));
      } else if (p < 0.66) {
        const t = ease(seg(p, 0.3, 0.46));
        const cx = 330;
        const cy = 170;
        const u = 1 - t;
        pos = {
          x: u * u * LIFT.x + 2 * u * t * cx + t * t * EAR.x,
          y: u * u * LIFT.y + 2 * u * t * cy + t * t * EAR.y,
          r: lerp(LIFT.r, EAR.r, t) - Math.sin(t * Math.PI) * 18,
          s: lerp(LIFT.s, EAR.s, t) + Math.sin(t * Math.PI) * 0.08
        };
      } else if (p < 0.85) {
        pos = mix(EAR, OUT, ease(seg(p, 0.66, 0.76)));
      } else {
        pos = mix(OUT, EAR, ease(seg(p, 0.85, 0.95)));
      }
      this.bud.setAttribute(
        'transform',
        `translate(${pos.x.toFixed(2)} ${pos.y.toFixed(2)}) rotate(${pos.r.toFixed(2)}) scale(${pos.s.toFixed(3)})`
      );
      const swap = seg(p, 0.17, 0.22);
      this.bud.setAttribute('opacity', swap.toFixed(3));
      if (this.budTop) this.budTop.setAttribute('opacity', (1 - swap).toFixed(3));

      // 4) Sonando sobre la oreja: ondas hacia el oído; el entorno se sigue oyendo
      const onEar = (p >= 0.46 && p < 0.67) || p >= 0.95;
      this.svg.classList.toggle('is-playing', onEar);
      this.svg.classList.toggle('is-anc', onEar);
      if (this.noise) this.noise.style.opacity = onEar ? '.45' : '1';
    }
  }

  /* ---------- Demo de cancelación de ruido ---------- */
  class AncDemo {
    constructor(el) {
      this.el = el;
      this.canvas = $('canvas', el);
      this.ctx = this.canvas.getContext('2d');
      this.db = $('[data-anc-db]', el);
      this.label = $('[data-anc-label]', el);
      this.desc = $('[data-anc-desc]', el);
      this.buttons = $$('[data-anc-mode]', el);
      this.modes = {
        off: { amp: 1, noise: 1, db: 78 },
        anc: { amp: 0.07, noise: 0, db: 33 },
        transparency: { amp: 0.45, noise: 0.15, db: 58 }
      };
      this.state = { amp: 1, noise: 1, db: 78 };
      this.target = this.modes.off;
      this.t = 0;
      this.visible = false;

      this.buttons.forEach((b) => b.addEventListener('click', () => this.setMode(b.dataset.ancMode)));
      this.resize = this.resize.bind(this);
      window.addEventListener('resize', this.resize);
      this.resize();

      new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible && !this.raf) this.loop();
      }).observe(el);

      this.setMode(el.dataset.ancStart || 'off');
    }

    setMode(mode) {
      if (!this.modes[mode]) return;
      this.target = this.modes[mode];
      this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.ancMode === mode)));
      const btn = this.buttons.find((b) => b.dataset.ancMode === mode);
      if (btn && this.desc) this.desc.textContent = btn.dataset.desc || '';
      if (btn && this.label) this.label.textContent = btn.dataset.label || '';
      if (reduceMotion) {
        this.state = { ...this.target };
        this.draw();
      }
    }

    resize() {
      const r = this.canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = r.width * dpr;
      this.canvas.height = r.height * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.w = r.width;
      this.h = r.height;
      this.draw();
    }

    loop() {
      if (!this.visible) {
        this.raf = null;
        return;
      }
      this.t += 0.016;
      ['amp', 'noise', 'db'].forEach((k) => (this.state[k] = lerp(this.state[k], this.target[k], 0.06)));
      this.draw();
      this.raf = requestAnimationFrame(() => this.loop());
    }

    draw() {
      const { ctx, w, h } = this;
      if (!w) return;
      ctx.clearRect(0, 0, w, h);
      const styles = getComputedStyle(document.documentElement);
      const c1 = styles.getPropertyValue('--accent').trim() || '#7c5cff';
      const c2 = styles.getPropertyValue('--accent-2').trim() || '#22e1ff';
      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, c1);
      grad.addColorStop(1, c2);
      const lines = 6;
      const mid = h * 0.42;
      for (let l = 0; l < lines; l++) {
        ctx.beginPath();
        ctx.lineWidth = l === 0 ? 3 : 1.5;
        ctx.strokeStyle = l === 0 ? grad : `rgba(255,255,255,${0.08 + l * 0.03})`;
        for (let x = 0; x <= w; x += 4) {
          const k = x / w;
          const env = Math.sin(k * Math.PI);
          const base = Math.sin(k * 10 + this.t * 3 + l * 0.7) * 0.6 + Math.sin(k * 23 - this.t * 4 + l) * 0.4;
          const jag = (Math.sin(k * 97 + this.t * 31 + l * 3) + Math.sin(k * 151 - this.t * 23)) * 0.5 * this.state.noise;
          const y = mid + (base + jag) * env * h * 0.22 * this.state.amp * (1 - l * 0.1);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      if (this.db) this.db.firstChild.nodeValue = Math.round(this.state.db);
    }
  }

  /* ---------- Comparación open-ear ---------- */
  function initOpenEar(root = document) {
    $$('[data-openear]', root).forEach((el) => {
      const set = (mode) => {
        el.classList.toggle('is-open', mode === 'open');
        $$('[data-openear-mode]', el).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.openearMode === mode)));
        $$('[data-openear-list]', el).forEach((l) => l.setAttribute('aria-hidden', String(l.dataset.openearList !== mode)));
      };
      $$('[data-openear-mode]', el).forEach((b) =>
        b.addEventListener('click', () => {
          el.dataset.touched = '1';
          set(b.dataset.openearMode);
        })
      );
      // La primera vez que se ve, cambia sola al M75 para mostrar la diferencia
      if ('IntersectionObserver' in window && !reduceMotion) {
        const io = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) return;
            io.disconnect();
            setTimeout(() => {
              if (!el.dataset.touched) set('open');
            }, 1600);
          },
          { threshold: 0.5 }
        );
        io.observe(el);
      }
    });
  }

  /* ---------- Videos UGC (reproducir al estar en pantalla) ---------- */
  function initUgc() {
    const cards = $$('[data-ugc-card]');
    if (!cards.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = $('video', entry.target);
          if (!video) return;
          if (entry.isIntersecting && !reduceMotion) {
            video.play().then(() => entry.target.classList.add('is-playing')).catch(() => {});
          } else {
            video.pause();
            entry.target.classList.remove('is-playing');
          }
        });
      },
      { threshold: 0.6 }
    );
    cards.forEach((card) => {
      const video = $('video', card);
      if (!video) return;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      io.observe(card);
      card.addEventListener('click', () => {
        if (video.paused) {
          video.play();
          card.classList.add('is-playing');
        } else {
          video.muted = !video.muted;
        }
      });
    });
  }

  /* ==========================================================================
     Carrito (drawer AJAX con Section Rendering API)
     ========================================================================== */
  const Cart = {
    init() {
      this.drawer = $('[data-cart-drawer]');
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (opener && this.drawer && theme.cartType !== 'page') {
          e.preventDefault();
          this.open(opener);
        }
        if (e.target.closest('[data-cart-close]')) this.close();
        const change = e.target.closest('[data-line-change]');
        if (change) {
          e.preventDefault();
          this.change(change.dataset.line, parseInt(change.dataset.qty, 10));
        }
      });
      document.addEventListener('change', (e) => {
        const input = e.target.closest('[data-line-input]');
        if (input) this.change(input.dataset.line, Math.max(0, parseInt(input.value, 10) || 0));
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer && this.drawer.classList.contains('is-open')) this.close();
      });
    },

    open(trigger) {
      if (!this.drawer) return;
      this.lastFocus = trigger || document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const close = $('[data-cart-close]', this.drawer.querySelector('.drawer__panel'));
      if (close) setTimeout(() => close.focus(), 50);
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    },

    render(sections) {
      const html = sections && sections['cart-drawer'];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-cart-content]', doc);
      const current = this.drawer && $('[data-cart-content]', this.drawer);
      if (fresh && current) current.replaceWith(fresh);
      const count = fresh ? fresh.dataset.itemCount : null;
      if (count != null) {
        $$('[data-cart-count]').forEach((el) => {
          el.textContent = count;
          el.dataset.count = count;
          el.classList.remove('bump');
          void el.offsetWidth;
          el.classList.add('bump');
        });
      }
    },

    async add(form, button) {
      const body = new FormData(form);
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
      button && button.classList.add('is-loading');
      try {
        const res = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
        this.render(data.sections);
        if (theme.cartType === 'page') {
          window.location.href = theme.routes.cart;
          return;
        }
        this.open(button);
      } catch (err) {
        toast(err.message || theme.strings.error, true);
      } finally {
        button && button.classList.remove('is-loading');
      }
    },

    async change(line, quantity) {
      if (this.drawer) this.drawer.classList.add('is-loading');
      try {
        const res = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: parseInt(line, 10), quantity, sections: ['cart-drawer'], sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
        this.render(data.sections);
        if (document.body.classList.contains('template-cart')) window.location.reload();
      } catch (err) {
        toast(err.message || theme.strings.error, true);
      } finally {
        if (this.drawer) this.drawer.classList.remove('is-loading');
      }
    }
  };
  theme.cart = Cart;

  /* ==========================================================================
     Ficha de producto (variantes, packs, stock, entrega, sticky)
     ========================================================================== */
  class BuyBox {
    constructor(el) {
      this.el = el;
      this.form = $('form[data-product-form]', el);
      const json = $('[data-variants]', el);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.idInput = this.form ? $('input[name="id"]', this.form) : null;
      this.qtyInput = $('[data-qty-input]', el);
      this.addBtn = $('[data-add]', el);
      this.addLabel = this.addBtn ? $('[data-add-label]', this.addBtn) : null;
      this.variant = this.variants.find((v) => this.idInput && String(v.id) === this.idInput.value) || this.variants[0];

      $$('[data-option-input]', el).forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
      $$('[data-bundle]', el).forEach((input) => input.addEventListener('change', () => this.onBundle(input)));
      $$('[data-qty-step]', el).forEach((btn) =>
        btn.addEventListener('click', () => {
          if (!this.qtyInput) return;
          const next = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + parseInt(btn.dataset.qtyStep, 10));
          this.qtyInput.value = next;
          this.syncBundles(next);
          this.updatePrice();
        })
      );
      if (this.qtyInput) this.qtyInput.addEventListener('change', () => this.updatePrice());

      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          if (theme.cartType === 'page' && !window.fetch) return;
          e.preventDefault();
          Cart.add(this.form, this.addBtn);
        });
      }

      this.initGallery();
      this.initDelivery();
      this.initSticky();
      const checked = $('[data-bundle]:checked', el);
      if (checked) this.onBundle(checked);
      this.update();
    }

    selectedOptions() {
      const groups = $$('[data-option-group]', this.el);
      return groups.map((g) => {
        const input = $('[data-option-input]:checked', g);
        return input ? input.value : null;
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      const match = this.variants.find((v) => v.options.every((o, i) => selected[i] == null || o === selected[i]));
      this.variant = match || null;
      if (this.variant && this.idInput) this.idInput.value = this.variant.id;
      $$('[data-option-value-label]', this.el).forEach((label) => {
        const group = label.closest('[data-option-group]');
        const input = group && $('[data-option-input]:checked', group);
        if (input) label.textContent = input.value;
      });
      if (this.variant && this.el.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (this.variant && this.variant.media) this.goToMedia(String(this.variant.media));
      this.update();
    }

    onBundle(input) {
      if (this.qtyInput) this.qtyInput.value = input.dataset.qty;
      this.updatePrice();
    }

    syncBundles(qty) {
      $$('[data-bundle]', this.el).forEach((b) => (b.checked = parseInt(b.dataset.qty, 10) === qty));
    }

    update() {
      const v = this.variant;
      const s = theme.strings || {};
      if (this.addBtn) {
        const available = v && v.available;
        this.addBtn.disabled = !available;
        this.addBtn.dataset.available = available ? 'true' : 'false';
      }
      this.updatePrice();
      this.updateStock();
    }

    updatePrice() {
      const v = this.variant;
      if (!v) {
        if (this.addLabel) this.addLabel.textContent = theme.strings.unavailable;
        return;
      }
      const qty = this.qtyInput ? Math.max(1, parseInt(this.qtyInput.value, 10) || 1) : 1;
      const compare = v.compare > v.price ? v.compare : 0;

      $$('[data-price]', document).forEach((el) => {
        if (el.closest('[data-buybox]') === this.el || el.closest('[data-sticky-atc]')) el.textContent = formatMoney(v.price);
      });
      $$('[data-compare]', this.el).forEach((el) => {
        el.textContent = compare ? formatMoney(compare) : '';
        el.hidden = !compare;
      });
      $$('[data-save-badge]', this.el).forEach((el) => {
        el.textContent = compare ? `-${Math.round(((compare - v.price) / compare) * 100)}%` : '';
      });
      $$('[data-save-amount]', this.el).forEach((el) => {
        el.textContent = compare ? `${theme.strings.save} ${formatMoney(compare - v.price)}` : '';
        el.hidden = !compare;
      });

      // Totales de los packs (descuento mostrado = descuento automático configurado en Shopify)
      $$('[data-bundle]', this.el).forEach((b) => {
        const q = parseInt(b.dataset.qty, 10);
        const pct = parseFloat(b.dataset.discount || '0');
        // Igual que Shopify en CLP: el descuento se calcula por unidad y se redondea hacia abajo al peso
        const unitOff = Math.floor((v.price * pct) / 10000) * 100;
        const total = (v.price - unitOff) * q;
        const before = (compare || v.price) * q;
        const box = b.nextElementSibling;
        const now = box && $('[data-bundle-total]', box);
        const was = box && $('[data-bundle-was]', box);
        if (now) now.textContent = formatMoney(total);
        if (was) {
          was.textContent = before > total ? formatMoney(before) : '';
          was.hidden = !(before > total);
        }
      });

      if (this.addLabel) {
        if (!v.available) {
          this.addLabel.textContent = theme.strings.soldOut;
        } else {
          const bundle = $('[data-bundle]:checked', this.el);
          const pct = bundle && parseInt(bundle.dataset.qty, 10) === qty ? parseFloat(bundle.dataset.discount || '0') : 0;
          const total = (v.price - Math.floor((v.price * pct) / 10000) * 100) * qty;
          this.addLabel.textContent = `${theme.strings.addToCart} · ${formatMoney(total)}`;
        }
      }
    }

    updateStock() {
      const el = $('[data-stock]', this.el);
      if (!el) return;
      const v = this.variant;
      const threshold = parseInt(el.dataset.threshold || '10', 10);
      const text = $('[data-stock-text]', el);
      const bar = $('.stock__bar i', el);
      if (!v || !v.available) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      const low = v.tracked && v.inv > 0 && v.inv <= threshold;
      el.classList.toggle('is-low', low);
      if (text) text.textContent = low ? theme.strings.stockLow.replace('[count]', v.inv) : theme.strings.stockOk;
      if (bar) {
        bar.parentElement.hidden = !low;
        bar.style.setProperty('--w', `${Math.max(8, (v.inv / threshold) * 100)}%`);
      }
    }

    initGallery() {
      this.slides = $('[data-gallery-slides]', this.el);
      this.thumbs = $$('[data-gallery-thumb]', this.el);
      this.dots = $$('[data-gallery-dot]', this.el);
      if (!this.slides) return;
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goToMedia(t.dataset.mediaId)));
      let raf;
      this.slides.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const i = Math.round(this.slides.scrollLeft / Math.max(this.slides.clientWidth, 1));
            this.setActiveThumb(i);
          });
        },
        { passive: true }
      );
    }

    goToMedia(id) {
      if (!this.slides) return;
      const slides = $$('[data-media-id]', this.slides);
      const index = slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (index < 0) return;
      this.slides.scrollTo({ left: index * this.slides.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.setActiveThumb(index);
    }

    setActiveThumb(index) {
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
    }

    initDelivery() {
      const el = $('[data-delivery]', this.el);
      if (!el) return;
      const skipWeekends = el.dataset.skipWeekends !== 'false';
      const addBusinessDays = (date, days) => {
        const d = new Date(date);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          const day = d.getDay();
          if (!skipWeekends || (day !== 0 && day !== 6)) added++;
        }
        return d;
      };
      const locale = document.documentElement.lang || 'es';
      const fmt = (d) => d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
      const min = addBusinessDays(new Date(), parseInt(el.dataset.min || '1', 10));
      const max = addBusinessDays(new Date(), parseInt(el.dataset.max || '2', 10));
      const target = $('[data-delivery-text]', el);
      if (target) {
        target.innerHTML = el.dataset.template
          .replace('[min]', `<strong>${fmt(min)}</strong>`)
          .replace('[max]', `<strong>${fmt(max)}</strong>`);
      }
    }

    initSticky() {
      const bar = $('[data-sticky-atc]', this.el);
      if (!bar || !this.addBtn || !('IntersectionObserver' in window)) return;
      document.body.classList.add('has-sticky-atc');
      new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', passed);
        bar.setAttribute('aria-hidden', String(!passed));
        bar.toggleAttribute('inert', !passed);
      }).observe(this.addBtn);
    }
  }

  /* ---------- Arranque ---------- */
  function init() {
    initHeader();
    initReveal();
    initSpotlight();
    initCountdowns();
    initUgc();
    initOpenEar();
    Cart.init();
    $$('[data-hero]').forEach((el) => new Hero(el));
    $$('[data-anc-demo]').forEach((el) => new AncDemo(el));
    $$('[data-buybox]').forEach((el) => new BuyBox(el));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Recarga parcial dentro del editor de temas
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    $$('[data-hero]', e.target).forEach((el) => new Hero(el));
    $$('[data-anc-demo]', e.target).forEach((el) => new AncDemo(el));
    $$('[data-buybox]', e.target).forEach((el) => new BuyBox(el));
    initCountdowns();
    initUgc();
    initOpenEar(e.target);
  });
})();
